import { apiRequest } from "@/lib/api";
import { normalizePublicStore } from "@/lib/store-data";
import type {
  PublicOrderPayload,
  PublicOrderPixPreviewResponse,
  PublicOrderResponse,
} from "@/types/order";
import type { PublicStore } from "@/types/store";
import type { PublicOrderItemPayload } from "@/types/order";
import type { ConfigurationSnapshot } from "@/types/food-configuration";

export type GiftPromotionQuote = {
  public_id: string;
  name: string;
  description: string;
  required_quantity: number;
  matched_quantity: number;
  applications: number;
  gift: {
    product_id: number;
    product_name: string;
    variation_id: number | null;
    variation_name: string;
    quantity: number;
  };
};

export type ItemQuote = {
  unit_price: string;
  subtotal: string;
  quantity: number;
  option_selections: ConfigurationSnapshot["options"];
  configuration_snapshot: ConfigurationSnapshot;
};

export function quotePublicItems(slug: string, items: PublicOrderItemPayload[]) {
  return apiRequest<{items: ItemQuote[]; subtotal: string; currency: string; gift_promotions?: GiftPromotionQuote[]}>(
    `/orders/public/${encodeURIComponent(slug)}/quote/`,
    { method: "POST", body: JSON.stringify({items}) },
  );
}

export async function getPublicStore(
  slug: string,
) {
  const store = await apiRequest<PublicStore>(
    `/catalog/public/stores/${encodeURIComponent(slug)}/`,
  );

  return normalizePublicStore(store);
}

const CHECKOUT_ATTEMPT = "food-checkout-attempt";
type CheckoutAttempt = { fingerprint: string; key: string };
let checkoutAttempt: CheckoutAttempt | null = null;

function checkoutKey(slug: string, payload: PublicOrderPayload): string {
  const content = JSON.stringify({ slug, payload });
  // FNV-1a identifies a local retry without storing customer data. This is not
  // an authorization or financial hash: the server verifies SHA-256 of the
  // entire canonical request under the company lock. Works on DEV HTTP too.
  let hash = BigInt("0xcbf29ce484222325");
  const prime = BigInt("0x100000001b3");
  for (const byte of new TextEncoder().encode(content)) {
    hash = BigInt.asUintN(64, (hash ^ BigInt(byte)) * prime);
  }
  const fingerprint = `v2:${content.length}:${hash.toString(16)}`;
  if (checkoutAttempt?.fingerprint === fingerprint) return checkoutAttempt.key;
  try {
    const stored = JSON.parse(sessionStorage.getItem(CHECKOUT_ATTEMPT) ?? "null");
    if (stored?.fingerprint === fingerprint && typeof stored.key === "string" &&
        /^[\da-f]{8}-[\da-f]{4}-4[\da-f]{3}-[89ab][\da-f]{3}-[\da-f]{12}$/i.test(stored.key)) {
      checkoutAttempt = stored;
      return stored.key;
    }
  } catch { /* Restricted storage: retain retries in memory for this page. */ }
  // getRandomValues remains available on HTTP; randomUUID and subtle do not.
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, "0")).join("");
  const key = `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  checkoutAttempt = { fingerprint, key };
  try { sessionStorage.setItem(CHECKOUT_ATTEMPT, JSON.stringify(checkoutAttempt)); }
  catch { /* Storage is optional; the server still enforces idempotency. */ }
  return key;
}

export async function createPublicOrder(
  slug: string,
  payload: PublicOrderPayload,
  idempotent = false,
) {
  const key = idempotent ? await checkoutKey(slug, payload) : null;
  const order = await apiRequest<PublicOrderResponse>(
    `/orders/public/${slug}/`,
    {
      method: "POST",
      ...(key ? { headers: { "Idempotency-Key": key } } : {}),
      body: JSON.stringify(payload),
    },
  );
  if (key && checkoutAttempt?.key === key) {
    checkoutAttempt = null;
    try { sessionStorage.removeItem(CHECKOUT_ATTEMPT); }
    catch { /* A completed order must not become an error during cleanup. */ }
  }
  return order;
}

export function previewPublicOrderPix(
  slug: string,
  payload: PublicOrderPayload,
) {
  return apiRequest<PublicOrderPixPreviewResponse>(
    `/orders/public/${slug}/pix-preview/`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
  );
}
